import { useDepot } from '#~/depot/hooks.js';
import { INITIAL_BOOT_OBSERVATION } from '#~/depot/types.js';
import { useMachineNames } from '#~/agent/hooks.js';
import { useEffect, useLayoutEffect, useRef, type ReactElement } from 'react';
import { useLocation, useMatch } from 'react-router-dom';
import { shallow } from 'zustand/shallow';
import { AutoLinkLoadingScreen } from '../verify-auto-link/v2/auto-link-loading.js';
import type { AutoLinkState } from '../verify-auto-link/v2/auto-link-types.js';
import { BootErrorScreen } from './boot-error-screen.js';
import { isAwaitingUserBootPath, isQualifiedCommittedStudioReady } from './boot-committed-path.js';
import { BOOT_STALL_DEADLINE_MS, bootStallDecision } from './boot-stall.js';
import { msSinceLastBootProgress } from '#~/logger/forward.js';
import { getBootId } from '#~/logger/index.js';
// Spec 4 (D1): the overlay's dismissal is the earliest in-renderer signal that
// boot reached a usable shell, so it is where the document partition closes.
import {
  advanceRendererDocumentPhase,
  finishRendererDocument,
  isRendererStartupProfiling,
  markRendererMilestone,
  rendererDestinationForPath
} from '#~/boot/renderer-startup.js';
import { cn } from '#~/utils/cn.js';
import { DataSTag, Sel } from '#~/stag/index.js';
import {
  publishBootLifecycleSuccess,
  publishBootLifecycleFailure,
  readBootLifecycleTerminal,
  retryCurrentDocumentBoot
} from '../root/bootstrap-analytics.js';
import { getBootSnapshot, isLaunchBudgetOwnedByMain } from '../root/boot-budget.js';

/**
 * `<BootOverlay>` — the single-mount unified loading shell.
 *
 * Why a single overlay (per gyfky-W1 spec): the prior boot stack
 * cascaded `<AutoLinkLoadingScreen>` across three sibling mounts
 * (`<AutoLinkGuard>` → `<AutoLinkLoadingScreen>` instance #1 →
 * unmount → `<Root>` → `<Loader>` instance #2 → unmount →
 * `<Placeholder>` → `<Linker>` own spinner). Each transition was a
 * remount which produced a visible flash. The architectural fix is
 * ONE overlay node mounted ONCE above the protected route subtree,
 * gated by OPACITY (never unmounted during the boot sequence),
 * reading a UNION of readiness signals from the depot.
 *
 * Per `acknowledge-rules-publicly` (ai.knowledge/global):
 *   - Per `stay-in-scope`: this component owns the boot UX; the
 *     guard / root / placeholder no longer render their own
 *     loading screens.
 *   - Per `code-comments-required`: each non-obvious decision below
 *     references the rule or knowledge entry it follows.
 *   - Per `reference-rules-in-comments`: see comments below for
 *     specific rule citations.
 */

type BootLoadingReason =
  | 'error'
  | 'autolink-checking'
  | 'autolink-linking'
  | 'autolink-verifying'
  | 'autolink-error'
  | 'bootstrap'
  | 'hydration'
  | 'ready';

// Reason for the overlay being visible (or `ready` when not). Priority:
// a published boot error wins outright (it is what the overlay renders —
// BootErrorScreen replaces the loading screen below), then the auto-link
// layer over bootstrap over hydration — matches what the user can actually
// see going wrong. `autolink-error` surfaces the autolink layer's UX (red
// message + 5s hold) over downstream signals.
//
// `error` exists because a failed boot must be distinguishable from a booting
// one by the attribute alone: without it a boot that died on the inventory
// read sat on the error screen reporting `bootstrap`, so the e2e settle helper
// (boot-settle.ts, which keys its one bounded Try-Again on `error`) read four
// win CI runs as a silent 2-minute wedge (33415915344 … 33536611420).
// Per rule: reference-rules-in-comments (ai.knowledge/global).
const computeReason = (
  autoLinkState: AutoLinkState,
  isRootReady: boolean,
  hasRegistryLoaded: boolean,
  bootError: boolean
): BootLoadingReason => {
  if (bootError) return 'error';
  if (autoLinkState.status === 'error') return 'autolink-error';
  if (autoLinkState.status === 'checking') return 'autolink-checking';
  if (autoLinkState.status === 'linking') return 'autolink-linking';
  if (autoLinkState.status === 'verifying_link') return 'autolink-verifying';
  if (!isRootReady) return 'bootstrap';
  if (!hasRegistryLoaded) return 'hydration';
  return 'ready';
};

/**
 * `<BootOverlay>` renders its `children` underneath an opacity-gated
 * full-viewport loading layer. The overlay node itself NEVER unmounts
 * during the boot sequence — its `data-testid="boot-overlay"` DOM
 * identity is stable across every Guard→Root→Placeholder transition
 * (asserted by `boot-overlay.regression.test.tsx`).
 *
 * `children` is the protected route subtree's `<Outlet />`-rendered
 * content; studio components mount underneath the overlay from the
 * first frame.
 */
export function BootOverlay({ children }: { readonly children: ReactElement }): ReactElement {
  const { friendlyName: computerName } = useMachineNames();
  // Read the union-of-signals slice from the depot. Per
  // `reference-rules-in-comments` (ai.knowledge/global): the `boot`
  // slice + `synchronizer.hasRegistryLoaded` are the engineer-named
  // dismiss inputs in the gyfky-W1 spec (Step 2b + gyfky knowledge
  // entry). `hasRegistryLoaded` lives on `synchronizer` and is NOT
  // duplicated under `boot`.
  const { pathname } = useLocation();
  const [
    autoLinkState,
    isRootReady,
    hasRegistryLoaded,
    bootError,
    observation,
    produce,
    accountId,
    roomRecords,
    hasUser
  ] = useDepot(
    (µ) =>
      [
        µ.boot.autoLinkState,
        µ.boot.isRootReady,
        µ.synchronizer.hasRegistryLoaded,
        µ.boot.error,
        µ.boot.observation ?? INITIAL_BOOT_OBSERVATION,
        // Bundled into the slice (not a separate `useDepot((µ) => µ.produce)`) so a
        // selector never returns a bare function — `useState`'s updater path would
        // otherwise invoke it. `produce` is a stable ref, so shallow-compare is unaffected.
        µ.produce,
        µ.user?.id ?? null,
        // Spec 4 (D3): the active room's own record is the room route's readiness
        // input; see activeRoomHydrated below. Appended to main's slice rather
        // than reordering it, so the destructuring stays aligned with the shape
        // ENG-4720 introduced.
        µ.roomRecords,
        !!µ.user
      ] as const,
    shallow
  );

  // Spec 4 (D3). The registry dimension used to be "the FULL room census is in
  // the depot" for every route. On a room route that is far more than the route
  // needs: measured on the reference account, the unscoped rooms.list behind
  // that census costs ~3.9s of server time (REPORT-D2) while the route itself
  // needs exactly one room's record — which the synchronizer now publishes from
  // a targeted read within ~250ms.
  //
  // `hasRegistryLoaded` KEEPS its meaning (full census) because the studio
  // switcher reads it as one: flipping it early against a partial map is what
  // would show "Create a studio" on a populated account. This predicate is
  // additive and room-route only.
  //
  // `permissions.canControl` must be a defined boolean: page.tsx renders
  // producer-vs-viewer chrome from it, so dismissing on an empty permissions
  // object would show viewer chrome to a producer. `hasUser` is required
  // because the rest of the shell hydrates from the published user.
  const roomRouteMatch = useMatch('/room/:roomId/*');
  const activeRoomRouteId = roomRouteMatch?.params.roomId;
  // Looked up by entry rather than by index: room ids are an opaque branded type
  // and the route param is a plain string, so indexing would need a type
  // assertion (rule: never-use-type-assertions, ai.knowledge/global).
  // `?? {}` because the depot slice can be read before the synchronizer has
  // published any room map at all — on the very first render, and in every
  // suite whose store fixture predates this selector. Per rule:
  // absent-data-gates-permissively (ai.knowledge/global), absent data must not
  // throw and must not claim readiness: with no map there is no active room
  // record, `activeRoomHydrated` stays false, and `registryReady` falls back to
  // the full-census signal — exactly main's behaviour.
  const activeRoomRecord = activeRoomRouteId
    ? Object.entries(roomRecords ?? {}).find(([id]) => id === activeRoomRouteId)?.[1]
    : undefined;
  const activeRoomHydrated =
    hasUser &&
    activeRoomRecord !== undefined &&
    activeRoomRecord.permissions.canControl !== undefined;
  const registryReady = hasRegistryLoaded || activeRoomHydrated;

  // One-way "boot completed" latch. Once the overlay has fully dismissed at
  // least once (reached `ready` / visible === false), transient re-entry into
  // an auto-link in-progress state must NOT re-raise the opaque pointer-blocking
  // overlay. On an already-booted, already-linked app, `bridge.conn` churn
  // (offline → online) re-pokes the auto-link controller, which republishes
  // `checking`; without this latch the full-screen overlay re-raised at
  // opacity-100 and intercepted the user's clicks (root cause of the mac
  // sh-sign-out + win boot-overlay e2e failures). A ref (not state) because
  // the latch is monotonic and must not itself trigger a re-render.
  const hasDismissedOnce = useRef(false);

  const autoLinkInProgress =
    autoLinkState.status === 'checking' ||
    autoLinkState.status === 'linking' ||
    autoLinkState.status === 'verifying_link';

  // Dismiss logic: the overlay stays up until autolink completes AND
  // the registry is loaded AND Root has finished (isRootReady). On the index
  // route, Root navigates to /room/boot/:id — the overlay must stay up until
  // that navigation fires, otherwise the 300ms opacity transition exposes the
  // Placeholder underneath during the route change.
  //
  // Before first dismissal, in-progress auto-link states block (cold boot needs
  // the overlay up). After first dismissal, only a genuine `error` re-raises the
  // auto-link dimension — a transient re-entry into checking/linking/verifying is
  // ignored so a re-pinged controller can't steal pointer events post-boot.
  const autoLinkBlocking =
    autoLinkState.status === 'error' || (autoLinkInProgress && !hasDismissedOnce.current);
  // Boot error forces overlay visible so the error screen shows
  const visible = autoLinkBlocking || !registryReady || !isRootReady || !!bootError;

  // Onboarding must follow the overlay's actual ownership, not reconstruct it
  // from Root/registry readiness while auto-link is still blocking. Publish
  // before paint so two setup surfaces never appear together (QA v2 #002).
  useLayoutEffect(() => {
    produce((draft) => {
      draft.boot.isOverlayVisible = visible;
    });
  }, [produce, visible]);

  // Set the latch the first time the overlay fully dismisses. Runs after render
  // so `visible` reflects the committed value; the next render that sees a
  // re-entrant `checking` reads `hasDismissedOnce.current === true` and keeps
  // the overlay dismissed (opacity-0 + pointer-events-none).
  useEffect(() => {
    if (!visible) {
      hasDismissedOnce.current = true;

      // Spec 4 (D1). Instrumentation ONLY: `visible`, `hasDismissedOnce` and
      // `isRootReady` are untouched above, and every recorder call is a no-op
      // after the first (milestone first-occurrence guard, forward-only phase
      // advance, idempotent finish), so overlay re-entry after the dismissal
      // latch cannot double-emit.
      markRendererMilestone('overlay-ready-committed');
      advanceRendererDocumentPhase('until-usable');

      // Overlay dismissal is a PROXY endpoint: the 300ms opacity transition has
      // not finished and the toolbar manifest may still be resolving, so this is
      // not the observed-controls SLO. In a profiling run the partition is left
      // open for the harness to close at the stricter endpoint it can actually
      // observe (review-4 §5 steps 3 and 7).
      if (!isRendererStartupProfiling()) {
        finishRendererDocument({
          outcome: 'usable',
          endMs: performance.now(),
          endpoint: 'overlay-ready',
          destination: rendererDestinationForPath(window.location.pathname)
        });
      }
    }
  }, [visible]);

  // A boot that ends on the error screen is a recorded FAILURE sample, never an
  // omitted one: dropping it would flatter the median exactly where the product
  // is worst. Separate effect because the dismissal path above never runs while
  // `bootError` holds the overlay visible.
  useEffect(() => {
    if (!bootError) {
      return;
    }

    finishRendererDocument({
      outcome: 'error',
      endMs: performance.now(),
      endpoint: 'overlay-ready',
      destination: rendererDestinationForPath(window.location.pathname)
    });
  }, [bootError]);
  const retryCurrentDocument = (): void => {
    produce((d) => {
      d.boot.error = null;
    });
    retryCurrentDocumentBoot();
  };

  // Overlay is the one renderer publisher of committed Studio success.
  // Navigate is not readiness; setup/prequal dismiss is awaiting_user.
  // Pathname comes from the router so action→room transitions re-run this.
  useEffect(() => {
    if (bootError) {
      return;
    }
    if (isAwaitingUserBootPath(pathname)) {
      produce((draft) => {
        const current = draft.boot.observation;
        // Settle once: replacing an unchanged outcome retriggers reactive subscribers.
        if (current?.outcome !== 'pending') {
          return;
        }
        draft.boot.observation = {
          ...current,
          outcome: 'awaiting_user',
          registry: hasRegistryLoaded ? 'loaded' : current.registry,
          bootId: getBootId()
        };
      });
      return;
    }
    if (visible) {
      return;
    }
    const bootId = getBootId();
    // Failure wins: a consumed failed slot must never become current-document ready.
    if (readBootLifecycleTerminal() === 'failed' || observation.outcome === 'failed') {
      return;
    }
    if (
      !isQualifiedCommittedStudioReady({
        pathname,
        observation,
        currentAccountId: accountId
      })
    ) {
      return;
    }
    // Current-document ready is independent of the launch telemetry claim: a
    // desktop reload reuses the same boot_id, so the claim below is already
    // consumed while this document still has to show a ready Studio.
    // The qualifier passing and the terminal still not settling is the exact
    // shape this boot fails in, and the two calls below are where that is
    // decided — so record their answers instead of inferring them. `attempt`
    // separates the two very different causes: a budget that REFUSED this
    // attempt still names one, while a renderer whose budget never installed
    // (an auxiliary window, or an entry that bailed before arming it) has no
    // attempt at all and can never settle a launch, however healthy the studio.
    const terminalBefore = readBootLifecycleTerminal();
    const published = terminalBefore === 'succeeded' || publishBootLifecycleSuccess();
    if (!published) {
      console.info(
        `[BootOverlay] commit-refused terminal=${terminalBefore ?? 'none'} ` +
          `attempt=${getBootSnapshot()?.attemptId ?? 'none'} ` +
          `outcome=${getBootSnapshot()?.outcome ?? 'null'} path=${pathname}`
      );
      return;
    }
    produce((draft) => {
      const current = draft.boot.observation ?? INITIAL_BOOT_OBSERVATION;
      // Preserve observation identity once ready to avoid reactive feedback.
      if (current.outcome === 'ready') {
        return;
      }
      draft.boot.observation = {
        ...current,
        outcome: 'ready',
        registry: hasRegistryLoaded ? 'loaded' : current.registry,
        bootId
      };
    });
  }, [
    visible,
    bootError,
    produce,
    hasRegistryLoaded,
    // Subscribe to readiness facts; source counts are sampled only when success commits.
    observation.outcome,
    observation.access,
    observation.roomFacts,
    observation.roomId,
    observation.accountId,
    pathname,
    accountId
  ]);

  // `data-loading-state` must agree with `visible`: when the overlay is
  // dismissed (post-latch re-entry into a transient auto-link state), report
  // `ready` so devtools / e2e selectors keying on `data-loading-state="ready"`
  // don't see a spurious flip back to `autolink-checking` on bridge.conn churn.
  // MERGE DECISION (ENG-4720 × ENG-4722): main added the bootError dimension to
  // computeReason; Spec 4 (D3) broadened the registry dimension to `registryReady`
  // (full census OR a hydrated active room). Both apply, and the reason MUST read
  // the same registry condition `visible` does — otherwise a room-route dismissal
  // reports 'hydration' while the overlay is already gone, and the e2e selectors
  // that key on data-loading-state read a state the user never sees.
  const reason = visible
    ? computeReason(autoLinkState, isRootReady, registryReady, !!bootError)
    : 'ready';

  // Single structured log line per transition. Per `code-comments-required`
  // (ai.knowledge/global): the WHY is production diagnosis of a stuck
  // boot — `console.info` survives prod log filters where `console.debug`
  // would be silenced.
  useEffect(() => {
    console.info(
      `[BootOverlay] state={autoLink:${autoLinkState.status}, isReady:${isRootReady}, ` +
        `hasRegistryLoaded:${hasRegistryLoaded}, activeRoomHydrated:${activeRoomHydrated}} → ` +
        `visible=${visible} reason=${reason}` +
        // A dismissed overlay that never commits readiness is the shape of a boot
        // that looks finished to the user and never settles its launch terminal —
        // no boot_succeeded, and the 307s deadline eventually claims it. When the
        // overlay is down, the committed-studio qualifier's inputs are the only
        // thing that explains why, and until now they were recorded nowhere.
        (visible
          ? ''
          : ` committed={path:${pathname}, obsRoom:${observation.roomId ?? 'none'}, ` +
            `access:${observation.access ?? 'none'}, facts:${observation.roomFacts ?? 'none'}, ` +
            `outcome:${observation.outcome}, obsAccount:${observation.accountId ?? 'none'}, ` +
            `account:${accountId ?? 'none'}}`)
    );
  }, [
    autoLinkState.status,
    isRootReady,
    hasRegistryLoaded,
    activeRoomHydrated,
    visible,
    reason,
    pathname,
    observation.roomId,
    observation.access,
    observation.roomFacts,
    observation.outcome,
    observation.accountId,
    accountId
  ]);

  // Overlay-local stall checks must not preempt the entry-owned launch clock.
  // BootBudgetGate presents the shared deadline on both desktop and browser.
  useEffect(() => {
    // Only arm while the overlay is up and no error is already showing. When the
    // overlay clears (visible → false) the cleanup disarms it, so a boot that
    // finishes before the deadline never trips the watchdog.
    // The entry-owned budget includes pre-Root work and excludes human waits.
    // Its gate owns timeout recovery; a wall-clock overlay timer must not
    // preempt it.
    if (!visible || bootError || getBootSnapshot() !== undefined || isLaunchBudgetOwnedByMain()) {
      return;
    }

    let timer: ReturnType<typeof setTimeout>;

    const check = (): void => {
      // An owner may have attached after this fallback timer was armed.
      if (getBootSnapshot() !== undefined) return;
      if (bootStallDecision(msSinceLastBootProgress()) === 'rearm') {
        // Boot is still advancing — wait another window before deciding.
        timer = setTimeout(check, BOOT_STALL_DEADLINE_MS);
        return;
      }

      const bootId = getBootId();
      const published = publishBootLifecycleFailure({ errorType: 'boot_timeout' });
      if (!published) {
        return;
      }
      produce((draft) => {
        // Don't clobber a real boot error raised elsewhere in the meantime.
        if (draft.boot.error) {
          return;
        }

        draft.boot.error = {
          message: 'Loading is taking longer than expected.',
          detail: 'The studio could not finish loading. Please try again.',
          severity: 'retryable',
          retryFn: retryCurrentDocument
        };
        const current = draft.boot.observation;
        if (current && current.outcome !== 'ready') {
          draft.boot.observation = {
            ...current,
            outcome: 'failed',
            bootId
          };
        }
      });
    };

    timer = setTimeout(check, BOOT_STALL_DEADLINE_MS);

    return () => clearTimeout(timer);
  }, [visible, bootError, produce]);

  return (
    <>
      <div
        data-testid='boot-overlay'
        {...DataSTag(Sel.BootOverlay, 'overlay')}
        data-loading-state={reason}
        // Opacity-only transition, NEVER unmount. bg-dark ensures the
        // overlay obscures content from frame 1.
        className={cn(
          'duration-300 fixed inset-0 z-50 bg-dark transition-opacity',
          visible ? 'opacity-100' : 'pointer-events-none opacity-0'
        )}
        aria-hidden={visible ? 'false' : 'true'}
      >
        {bootError ? (
          <BootErrorScreen source='boot-overlay' error={bootError} />
        ) : (
          <AutoLinkLoadingScreen
            state={autoLinkState}
            zeroStudioSetup={
              autoLinkState.status === 'checking' ||
              autoLinkState.status === 'linking' ||
              autoLinkState.status === 'verifying_link' ||
              autoLinkState.status === 'success'
            }
            computerName={computerName ?? undefined}
          />
        )}
      </div>
      {children}
    </>
  );
}
